<template>
    <div class="card w-75">
        <div class="card-header">
            <div class="d-flex justify-content-between align-items-center">
                <h3 class="card-header-title">
                    {{ $t('Service+ Unlock') }}
                </h3>
            </div>
        </div>

        <div class="card-body">
            <div class="card mb-4 me-2 float-start" style="width: 35rem">
                <div class="card-body">
                    <div class="mb-1">
                        <label class="col-form-label-sm" for="serial_numbers">
                            {{ $t('Enter 1-50 serial numbers:') }}
                            <i
                                class="bi bi-info-circle-fill text-primary px-1"
                                v-tooltip="$t('Enter serial numbers 1 per line or separate by commas.')">
                            </i>
                        </label>
                        <div>
                            <textarea
                                id="serial_numbers"
                                v-model="entered_serials"
                                class="form-control input-group-sm"
                                style="height: 235px"
                            ></textarea>
                        </div>
                    </div>
                </div>
                <div class="m-2 text-end">
                    <button type="button" class="btn btn-sm btn-primary" @click="validate">
                        {{ $t('Generate Codes') }}
                    </button>
                </div>
            </div>

            <template v-if="creditsVisible">
                <div class="card mb-4 me-2 float-start" style="width: 25rem">
                    <div class="card-header mt-2 py-1">
                        <h5>{{ $t('Generated Codes') }}:</h5>
                    </div>
                    <div class="card-body">
                        <div class="border overflow-auto" style="height: 200px">
                            <table class="table table-sm table-hover table-bordered text-nowrap">
                                <thead>
                                <tr>
                                    <th><b>{{ $t("Serial") }}</b></th>
                                    <th><b>{{ $t("Service+ Code") }}</b></th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="item in generated_list" :key="item.serial">
                                    <td>{{ item.serial }}</td>
                                    <td>{{ item.code }}</td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                        <hr />
                        <div class="m-2 text-end">
                            <a href="#" class="m-1" @click="export_codes()">
                                {{ $t('Export to CSV') }}
                            </a>
                        </div>
                    </div>
                </div>
            </template>

            <div class="mb-4 me-2 float-start">
                <AppLoading :loading="loading_count>0" />
            </div>
        </div>
    </div>

    <AppModal v-model="alertMessageVisible" :showHeader="false">
        <div class="text-danger">
            {{ alertMessageText }}
        </div>
        <div class="mt-3 text-end">
            <button type="button" class="btn btn-sm btn-primary" @click="close_alert_message">{{ $t("Ok") }}</button>
        </div>
    </AppModal>

    <AppModal v-model="invalidSNVisible" size="lg" :showHeader="false">
        <div>
            <p class="fw-bold">{{ $t('These serial numbers are invalid or not associated with your organization:') }}</p>
            {{ invalid_serials }}
        </div>
        <div class="mt-3 text-danger">
            {{ $t('Please update the list and try again.') }}
        </div>
        <div class="mt-3 text-end">
            <button type="button" class="btn btn-sm btn-primary" @click="invalidSNVisible=false">{{ $t("Ok") }}</button>
        </div>
    </AppModal>

    <AppModal v-model="alreadyGeneratedVisible" size="lg" :showHeader="false">
        <div>
            <p class="fw-bold">{{ $t('messages.ess_no_charge_notice') }}</p>
            {{ already_generated }}
        </div>
        <div class="mt-3 text-end">
            <button type="button" class="btn btn-sm btn-white" @click="alreadyGeneratedVisible=false">
                {{ $t('Cancel') }}
            </button>
            &nbsp;
            <button type="button" class="btn btn-sm btn-primary" @click="confirm_use_credits">
                {{ $t('Continue') }}
            </button>
        </div>
    </AppModal>

    <AppModal v-model="confirmUseCredits" size="lg" :showHeader="false">
        <div>
            <p class="fw-bold">{{ $t('messages.ess_using_credits', {credits}) }}</p>
        </div>
        <div class="mt-3 text-end">
            <button type="button" class="btn btn-sm btn-white" @click="confirmUseCredits=false">
                {{ $t('No') }}
            </button>
            &nbsp;
            <button type="button" class="btn btn-sm btn-primary" @click="apply_credits">
                {{ $t('Yes') }}
            </button>
        </div>
    </AppModal>
</template>

<script setup>
import {trans as $t} from "@/vue/composables/missingTranslations";
import {ref} from "vue";
import {toast} from "vue3-toastify";
import axios from "axios";
import AppLoading from "@/vue/shared/AppLoading.vue";
import AppModal from "@/vue/shared/AppModal.vue";

const props = defineProps({
    id: {
        type: Number,
        required: true
    }
});

const alertMessageVisible = ref(false);
const alertMessageText = ref("");

const invalidSNVisible = ref(false);
const invalid_serials = ref("");

const alreadyGeneratedVisible = ref(false);
const already_generated = ref("");

const haveCredits = ref(false);

const confirmUseCredits = ref(false);
const credits = ref(0);

const loading_count = ref(0);
const entered_serials = ref("");

const creditsVisible = ref(false);

let compacted_serials = "";     // Compacted list of entered serials.
let generated_list = [];        // Complete list of serials and codes after applying credits.
let pending_generated_list = [];

const validate = () => {
    creditsVisible.value = false;

    compact_serials();

    if( compacted_serials == '' ) {
        alertMessageText.value = $t("Missing serial numbers.");
        alertMessageVisible.value = true;
        return false;
    }

    const regex = /^([A-Z0-9]{1,12})(,[A-Z0-9]{1,12})*$/;
    if( !regex.test(compacted_serials) ) {
        alertMessageText.value = $t("Serial numbers can only be numbers and letters up to 12 characters long.");
        alertMessageVisible.value = true;
        return false;
    }

    const serialsArray = compacted_serials.split(",");
    if( serialsArray.length > 50 ) {
        alertMessageText.value = $t("Cannot activate more than 50 serial numbers at a time.");
        alertMessageVisible.value = true;
        return false;
    }

    loading_count.value++;
    axios.post(`/api/orgs/${props.id}/ess/validate`, { serials: compacted_serials })
        .then((response) => {
            if( response.status === 200 ) {
                process_validated(response.data);
            }
            else {
                toast.error($t("Validation failed."));
            }
        }).catch((error) => {
        if( error?.response?.data?.error ) {
            toast.error(error.response.data.error);
        }
        else {
            console.error(error);
            toast.error($t("Something went wrong. Try again later"));
        }
    }).finally(() => {
        loading_count.value--;
    });
};

const compact_serials = () => {
    const serials = entered_serials.value.replace(/,/g, ' ').toUpperCase().trim().split(/[\n\r\s]+/);

    // Rebuild into a compacted string
    compacted_serials = "";
    for( let s=0; s<serials.length; s++ ) {
        compacted_serials += serials[s] +",";
    }
    compacted_serials = compacted_serials.replace(/,$/, '');
}

const process_validated = (data) => {
    haveCredits.value = data.have_credits;
    credits.value = data.credits_needed;
    pending_generated_list = [];

    if( data.invalids.length > 0 ) {
        invalid_serials.value = data.invalids.join(", ");
        invalidSNVisible.value = true;
        return;
    }
    if( data.already_generated.length > 0 ) {
        if( data.have_credits === false ) {
            pending_generated_list = data.already_generated_codes ?? [];
        }
        already_generated.value = data.already_generated.join(", ");
        alreadyGeneratedVisible.value = true;
        return;
    }

    confirm_use_credits();
};

const confirm_use_credits = () => {
    alreadyGeneratedVisible.value = false;

    if( haveCredits.value === false ) {
        alertMessageText.value = $t('messages.ess_not_enough_credits');
        alertMessageVisible.value = true;
        return;
    }

    if( credits.value > 0 ) {
        confirmUseCredits.value = true;
    }
    else {
        apply_credits();
    }
};

const close_alert_message = () => {
    alertMessageVisible.value = false;

    if( pending_generated_list.length > 0 ) {
        display_credits({ generated: pending_generated_list });
        pending_generated_list = [];
    }
};

const apply_credits = () => {
    confirmUseCredits.value = false;

    loading_count.value++;
    axios.post(`/api/orgs/${props.id}/ess/apply_credits`, { serials: compacted_serials })
        .then((response) => {
            if( response.status === 200 ) {
                process_applied_credits(response.data);
            }
            else {
                toast.error($t("Failed to apply credits."));
            }
        }).catch((error) => {
        if( error?.response?.data?.error ) {
            toast.error(error.response.data.error);
        }
        else {
            console.error(error);
            toast.error($t("Something went wrong. Try again later"));
        }
    }).finally(() => {
        loading_count.value--;
    });
};

const process_applied_credits = (data) => {
    if( (data.credit_blocked ?? []).length > 0 ) {
        pending_generated_list = data.generated ?? [];
        alertMessageText.value = $t('messages.ess_not_enough_credits');
        alertMessageVisible.value = true;
        return;
    }

    display_credits(data);
};

const display_credits = (data) => {
    generated_list = data.generated;
    creditsVisible.value = true;
};

const export_codes = () => {
    let csvContent = [];

    let headerRow = ['"Serial"', '"Service+ Code"'];

    csvContent.push(headerRow.join(','));

    // Collect data from each row of Generated Codes
    generated_list.forEach((item) => {
        let row = ['"' + item.serial + '"', '"\t' + item.code + '"'];
        csvContent.push(row.join(','));
    });

    // Create a link to download a CSV file
    let csvData = csvContent.join('\n');
    let blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    let link = document.createElement('a');
    let fileName = 'generated-codes.csv';
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', fileName);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
</script>
